/* eslint-disable no-console */
import { env } from 'next-runtime-env';

import { ErrorResponse, HttpStatusEnum } from '../types';
import { handleSignIn } from '../../lib';

//TODO:Read From env
const TIME_OUT = 60000;

export interface FetchConfig extends RequestInit {
  baseURL?: string;
  timeout?: number;
  params?: Record<string, any>;
  responseType?: 'json' | 'blob' | 'text';
  url?: string;
  headers?: Record<string, string>;
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
}

export interface FetchResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  config: FetchConfig;
}

const buildURL = (url: string, baseURL?: string, params?: Record<string, any>): string => {
  const base = baseURL || '';
  const fullURL = url.startsWith('http') ? url : `${base}${url}`;

  if (!params) return fullURL;

  const urlObj = new URL(fullURL);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlObj.searchParams.append(key, String(value));
    }
  });

  return urlObj.toString();
};

const handleRequest = (config: FetchConfig) => {
  if (env('NEXT_PUBLIC_LOGS_ENABLE') === 'true') {
    const body = config.body
      ? typeof config.body === 'string'
        ? JSON.parse(config.body)
        : config.body
      : undefined;
    console.log(`🚀 Request: '${config.url}':`, {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      headers: config.headers,
      params: config.params,
      data: body,
      timestamp: new Date().toISOString(),
    });
  }
  return config;
};

const handleResponse = <T>(response: FetchResponse<T>) => {
  if (env('NEXT_PUBLIC_LOGS_ENABLE') === 'true')
    console.log(`✅ Response: '${response.config.url}':`, {
      status: response.status,
      statusText: response.statusText,
      method: response.config.method?.toUpperCase(),
      data: response.data,
      headers: Object.fromEntries(response.headers.entries()),
      timestamp: new Date().toISOString(),
    });
  return response;
};

const handleResponseError = async (error: any, status?: number): Promise<never> => {
  if (status === 401) {
    handleSignIn()
  }
  let errorResponse: ErrorResponse = new ErrorResponse(
    status as HttpStatusEnum,
    'GeneralError',
    'خطایی رخ داده است لطفا بعدا تلاش کنید.',
    [
      {
        code: '100',
        message: 'خطایی رخ داده است لطفا بعدا تلاش کنید.',
      },
    ]
  );

  try {
    const body = (await error.json()) as Partial<ErrorResponse>;
    errorResponse = new ErrorResponse(
      body.status ?? errorResponse.status,
      body.type ?? errorResponse.type,
      body.detail ?? errorResponse.detail,
      body.errors ?? errorResponse.errors
    );
  } catch (error) {
    console.log(error);
  }
  return Promise.reject(errorResponse);
};

const createProgressTrackingBody = (
  body: BodyInit,
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void
): { body: BodyInit; contentLength?: number } => {
  if (!onUploadProgress) {
    return { body };
  }

  // For FormData we send the raw body and let the browser set the multipart
  // Content-Type/Content-Length (with boundary). `fetch` cannot reliably report
  // granular upload progress, and streaming the body requires `duplex: 'half'`
  // plus HTTP/2, so we report an indeterminate start and complete on response.
  if (body instanceof FormData) {
    onUploadProgress({ loaded: 0, total: 1 });
    return { body };
  }

  // For string/JSON data
  if (typeof body === 'string') {
    const totalSize = new Blob([body]).size;
    onUploadProgress({ loaded: 0, total: totalSize });

    // For small string data, immediately report as complete
    setTimeout(() => {
      onUploadProgress({ loaded: totalSize, total: totalSize });
    }, 0);

    return { body, contentLength: totalSize };
  }

  // For other body types, just return as-is
  return { body };
};

const fetchWithTimeout = async (url: string, config: FetchConfig): Promise<Response> => {
  const timeout = config.timeout || TIME_OUT;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...config,
      signal: controller.signal,
      ...(typeof window !== 'undefined' && { credentials: 'include' }),
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

class FetchInstance {
  async request<T = any>(config: FetchConfig): Promise<FetchResponse<T>> {
    const baseURL = env('NEXT_PUBLIC_BASE_URL') || '';
    const processedConfig = handleRequest({
      ...config,
      baseURL: config.baseURL || baseURL,
    });

    const url = buildURL(config.url || '', processedConfig.baseURL, config.params);

    const finalConfig = { ...processedConfig };
    if (config.body && config.onUploadProgress) {
      const { body, contentLength } = createProgressTrackingBody(
        config.body,
        config.onUploadProgress
      );
      finalConfig.body = body;
      if (contentLength && finalConfig.headers) {
        (finalConfig.headers as any)['Content-Length'] = contentLength.toString();
      }
    }

    try {
      const response = await fetchWithTimeout(url, finalConfig);

      if (!response.ok) {
        return handleResponseError(response, response.status);
      }

      // FormData uploads can't report byte-level progress via fetch, so finish
      // the indeterminate progress once the response arrives.
      if (config.body instanceof FormData && config.onUploadProgress) {
        config.onUploadProgress({ loaded: 1, total: 1 });
      }

      let data: T = undefined as T;
      const responseType = config.responseType || 'json';

      const hasBody = response.status !== 204 && response.headers.get('content-length') !== '0';

      if (hasBody) {
        if (responseType === 'blob') {
          data = (await response.blob()) as T;
        } else if (responseType === 'text') {
          data = (await response.text()) as T;
        } else {
          const text = await response.text();
          data = text ? (JSON.parse(text) as T) : (undefined as T);
        }
      }

      const fetchResponse: FetchResponse<T> = {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        config: { ...processedConfig, url },
      };

      return handleResponse(fetchResponse);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  async get<T = any>(url: string, config?: Omit<FetchConfig, 'method'>): Promise<FetchResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: Omit<FetchConfig, 'method'>
  ): Promise<FetchResponse<T>> {
    let body: BodyInit | undefined = data;
    const headers: Record<string, string> = { ...config?.headers };

    if (!(data instanceof FormData)) {
      body = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
    }
    return this.request<T>({
      ...config,
      method: 'POST',
      url,
      body,
      headers,
      onUploadProgress: config?.onUploadProgress,
    });
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: Omit<FetchConfig, 'method'>
  ): Promise<FetchResponse<T>> {
    let body: BodyInit | undefined = data;
    const headers: Record<string, string> = { ...config?.headers };

    if (!(data instanceof FormData)) {
      body = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
    }

    return this.request<T>({
      ...config,
      method: 'PUT',
      url,
      body,
      headers,
      onUploadProgress: config?.onUploadProgress,
    });
  }

  async delete<T = any>(
    url: string,
    data?: any,
    config?: Omit<FetchConfig, 'method'>
  ): Promise<FetchResponse<T>> {
    let body: BodyInit | undefined = data;
    const headers: Record<string, string> = { ...config?.headers };

    if (!(data instanceof FormData)) {
      body = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
    }

    return this.request<T>({
      ...config,
      method: 'DELETE',
      url,
      body,
      headers,
    });
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: Omit<FetchConfig, 'method'>
  ): Promise<FetchResponse<T>> {
    let body: BodyInit | undefined = data;
    const headers: Record<string, string> = { ...config?.headers };

    if (!(data instanceof FormData)) {
      body = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
    }
    return this.request<T>({
      ...config,
      method: 'PATCH',
      url,
      body,
      headers,
      onUploadProgress: config?.onUploadProgress,
    });
  }
}

const fetchInstance = new FetchInstance();

export default fetchInstance;
